/**
 * Next.js App Component for WoodAlps Wooden Bicycles
 * 
 * This is the root component that wraps all pages in the application.
 * Responsibilities:
 * - Apply global styles (CSS and custom scrollbar)
 * - Set up font loading and CSS variables
 * - Provide default layout wrapper with override capability
 * - Initialize any global providers or contexts
 * - Preload translations to prevent flash of untranslated content
 * 
 * @description Main app wrapper with global styles and layout management
 */

import '../styles/globals.css';    // Global Tailwind and custom styles
import '../styles/scrollbar.css';  // Custom scrollbar styling
import Layout from '../components/layout/Layout';
import { roboto } from '../components/fonts';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Analytics } from '@vercel/analytics/react';

// Preload all translations on app initialization
const preloadTranslations = async (locale) => {
  try {
    const translationModule = await import(`../locales/${locale}.json`);
    return translationModule.default;
  } catch (error) {
    console.warn(`Failed to preload translations for locale: ${locale}`, error);
    return null;
  }
};

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const locale = router.locale || 'en';

  // Preload translations on locale change to prevent flash
  useEffect(() => {
    // Preload current locale translations
    preloadTranslations(locale);
  }, [locale]);

  // Allow pages to define custom layouts while providing sensible default
  const getLayout = Component.getLayout || ((page) => <Layout>{page}</Layout>);

  return (
    <div className={roboto.variable}>
      {getLayout(<Component {...pageProps} />)}
      <Analytics />
    </div>
  );
}

export default MyApp; 